/**
 * Parse a freeform date string into the canonical `YYYY-MM-DD` form
 * used by `<input type="date">` and our Firestore Timestamp helpers.
 *
 * Returns `''` (the canonical "unset") if the string is empty.
 * Returns `null` if the string is non-empty but unparseable, so callers
 * can distinguish "user cleared the field" from "user typed garbage".
 *
 * Accepted formats:
 *   - `YYYY-MM-DD`        (canonical)
 *   - `YYYY/MM/DD`        (Excel paste, common JP locale)
 *   - `YYYY.MM.DD`        (some Japanese sheets)
 *   - `YYYY年M月D日`       (with optional spaces; trailing 日 optional)
 *   - `M/D/YYYY`          (US-style — only when the trailing token is
 *                          a 4-digit year, otherwise we'd misread JP
 *                          `YYYY/M/D` as US)
 *
 * Single- and double-digit month/day are both accepted; padding is
 * applied on the way out. Years are clamped to [1900, 2100] so paste
 * accidents (e.g. zip codes) don't yield a 0042 date.
 */
export function parseFlexibleDate(raw: string): string | null {
  const s = raw.trim();
  if (!s) return '';

  let y: number | null = null;
  let mo: number | null = null;
  let d: number | null = null;

  // YYYY-MM-DD / YYYY/MM/DD / YYYY.MM.DD — leading 4-digit year wins
  // first because it's the unambiguous canonical form.
  let m = /^(\d{4})[-/.](\d{1,2})[-/.](\d{1,2})$/.exec(s);
  if (m) {
    y = Number(m[1]);
    mo = Number(m[2]);
    d = Number(m[3]);
  }

  // YYYY年M月D日 (日 optional, whitespace tolerated)
  if (!m) {
    m = /^(\d{4})\s*年\s*(\d{1,2})\s*月\s*(\d{1,2})\s*日?$/.exec(s);
    if (m) {
      y = Number(m[1]);
      mo = Number(m[2]);
      d = Number(m[3]);
    }
  }

  // M/D/YYYY (US) — only matches when year sits at the end with 4
  // digits, so JP-style `2026/1/2` falls through to the first branch.
  if (!m) {
    m = /^(\d{1,2})[/.](\d{1,2})[/.](\d{4})$/.exec(s);
    if (m) {
      mo = Number(m[1]);
      d = Number(m[2]);
      y = Number(m[3]);
    }
  }

  if (y == null || mo == null || d == null) return null;
  if (y < 1900 || y > 2100) return null;
  if (mo < 1 || mo > 12) return null;
  if (d < 1 || d > 31) return null;

  // Reject impossible day-of-month (e.g. Feb 30) so we don't silently
  // store a date the user didn't intend. Round-tripping through Date
  // catches the common cases.
  const probe = new Date(Date.UTC(y, mo - 1, d));
  if (
    probe.getUTCFullYear() !== y ||
    probe.getUTCMonth() !== mo - 1 ||
    probe.getUTCDate() !== d
  ) {
    return null;
  }

  return `${String(y).padStart(4, '0')}-${String(mo).padStart(2, '0')}-${String(
    d,
  ).padStart(2, '0')}`;
}
